// Bilingual story text — every DialogueLine/Whisper/stage-copy field that
// used to be a plain Korean string is now a `Loc` pair, picked by the current
// language at render time. Speaker/opponent NAMES and TITLES stay Korean
// throughout the data layer (act*.ts, stages.ts, Game.tsx's portrait maps all
// key off them) — only `dName`/`dTitle` translate them, at the render
// boundary, via the dictionaries below. This keeps the ~40 Korean-keyed
// portrait maps in Game.tsx untouched.

import { getLanguage, useLang, type LangCode } from "../../gamzone/i18n.ts";

export interface Loc {
  ko: string;
  en: string;
}

export function loc(x: Loc, lang: LangCode = getLanguage()): string {
  return x[lang];
}

/** React hook: resolve a Loc to the current language (re-renders on change). */
export function useLoc(x: Loc): string {
  const lang = useLang();
  return x[lang];
}

/** Canonical English names — matches the art asset filenames (e.g.
 *  09-ivra-moonshadow-dancer, 15-vela-rift-queen), not a literal
 *  transliteration of the Korean. */
const NAME_EN: Record<string, string> = {
  나: "Me",
  린: "Rin",
  브람: "Bram",
  세라: "Sera",
  토르: "Tor",
  니아: "Nia",
  칼데르: "Calder",
  오르나: "Orna",
  마레크: "Marek",
  이브라: "Ivra",
  로웬: "Rowen",
  드라벤: "Draven",
  실렌: "Silen",
  카이로스: "Kairos",
  아르덴: "Arden",
  벨라: "Vela",
  짐승: "Beast",
  "균열된 구슬": "Cracked Orb",
  상대: "Opponent",
};

const TITLE_EN: Record<string, string> = {
  "이끼 문지기": "Moss Gatekeeper",
  "등불 수집가": "Lantern Collector",
  "담쟁이 궁수": "Ivy Archer",
  "바람길 추적자": "Windtrail Tracker",
  "유리 구슬 점술가": "Glass Orb Seer",
  "협곡의 쌍검": "Twin Blades of the Gorge",
  "비문 해독사": "Inscription Scholar",
  "청동 거상": "Bronze Colossus",
  "달그림자 무희": "Moonshadow Dancer",
  "가시 재판관": "Thorn Judge",
  "붉은 창의 기사": "Knight of the Red Lance",
  "수정 사서": "Crystal Archivist",
  "황혼의 사냥꾼": "Dusk Hunter",
  "균열에 물든 파수꾼": "Rift-Tainted Guardian",
  "잿빛 균열의 왕비": "Ashen Queen of the Rift",
};

/** Localize a display name — the Korean key is used unchanged everywhere
 *  else (portrait maps, DialogueLine.name, opponentName, ...); this only
 *  swaps what's shown to the player. */
export function dName(ko: string, lang: LangCode = getLanguage()): string {
  if (lang !== "en") return ko;
  return NAME_EN[ko] ?? ko;
}

export function dTitle(ko: string, lang: LangCode = getLanguage()): string {
  if (lang !== "en") return ko;
  return TITLE_EN[ko] ?? ko;
}
